import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtMoney, photoUrl, getToken } from "@/lib/api";
import { API } from "@/constants";
import { Badge } from "@/admin/Dashboard";
import { inputClass, labelClass } from "@/pages/EnquiryPage";

const STATUS_TONE = { draft: "neutral", live: "green", under_offer: "blue", sold: "gold" };
const EMPTY = { category: "aircraft", title: "", year: "", price: "", currency: "GBP", discreet: false, location: "", specs: {}, summary: "", description: "", status: "draft", is_example: false };

export function ListingsAdmin() {
  const [items, setItems] = useState(null);
  useEffect(() => { adminApi("/admin/listings").then(setItems).catch(() => setItems([])); }, []);
  return (
    <div data-testid="admin-listings-page">
      <div className="flex items-end justify-between gap-4">
        <div><h1 className="font-serif text-3xl lg:text-4xl text-white">Listings</h1><p className="text-sm text-white/50 mt-1">Aircraft and yachts shown on the site.</p></div>
        <Link to="/admin/listings/new" data-testid="listing-new-button" className="inline-flex items-center gap-2 bg-gold text-black text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm hover:bg-gold-light"><Plus size={13} /> New listing</Link>
      </div>
      <div className="mt-6 bg-surface border border-white/10 rounded-sm divide-y divide-white/5">
        {(items || []).map((l) => (
          <Link key={l.id} to={`/admin/listings/${l.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-white/5" data-testid={`admin-listing-row-${l.id}`}>
            <div className="w-16 h-12 bg-white/5 rounded-sm overflow-hidden shrink-0">{l.photos?.[0] && <img src={photoUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{l.title} {l.is_example && <span className="text-white/30 text-xs">· example</span>}</p><p className="text-xs text-white/40">{l.category} · {l.year || "–"} · {l.location || "–"} · {l.discreet ? "POA" : fmtMoney(l.price, l.currency) || "POA"}</p></div>
            <Badge tone={STATUS_TONE[l.status]}>{l.status.replace("_", " ")}</Badge>
          </Link>
        ))}
        {items && !items.length && <p className="px-4 py-8 text-sm text-white/40 text-center">No listings yet.</p>}
      </div>
    </div>
  );
}

export function ListingEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [specText, setSpecText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef();

  useEffect(() => {
    if (isNew) return;
    adminApi(`/admin/listings/${id}`).then((l) => {
      setForm({ ...EMPTY, ...l, year: l.year ?? "", price: l.price ?? "" });
      setPhotos(l.photos || []);
      setSpecText(Object.entries(l.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
    }).catch(() => navigate("/admin/listings"));
  }, [id, isNew, navigate]);

  const payload = () => {
    const specs = {};
    specText.split("\n").forEach((line) => { const i = line.indexOf(":"); if (i > 0) specs[line.slice(0, i).trim()] = line.slice(i + 1).trim(); });
    const { photos: _p, created_at, updated_at, id: _id, source_lead_id, ...rest } = form; // eslint-disable-line no-unused-vars
    return { ...rest, year: form.year === "" ? null : Number(form.year), price: form.price === "" ? null : Number(form.price), specs };
  };

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const l = await adminApi(isNew ? "/admin/listings" : `/admin/listings/${id}`, { method: isNew ? "POST" : "PUT", body: payload() });
      toast.success("Listing saved");
      if (isNew) navigate(`/admin/listings/${l.id}`, { replace: true });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (isNew) return toast.error("Save the listing first, then add photos.");
    const list = Array.from(files).slice(0, 12 - photos.length);
    for (const f of list) {
      setUploading((u) => u + 1);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await fetch(`${API}/admin/listings/${id}/photos`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
        setPhotos((p) => [...p, data]);
      } catch (err) { toast.error(err.message); } finally { setUploading((u) => u - 1); }
    }
  };

  const makeCover = async (photoId) => {
    const order = [photoId, ...photos.filter((p) => p.id !== photoId).map((p) => p.id)];
    const res = await adminApi(`/admin/listings/${id}/photos/order`, { method: "PUT", body: { photo_ids: order } });
    setPhotos(res);
  };

  const removePhoto = async (photoId) => {
    await adminApi(`/admin/listings/${id}/photos/${photoId}`, { method: "DELETE" });
    setPhotos((p) => p.filter((x) => x.id !== photoId));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <form onSubmit={save} data-testid="admin-listing-editor">
      <Link to="/admin/listings" className="text-xs text-white/40 hover:text-white">← Listings</Link>
      <div className="mt-3 flex items-center justify-between gap-4">
        <h1 className="font-serif text-3xl text-white">{isNew ? "New listing" : form.title || "Listing"}</h1>
        <div className="flex gap-2">
          {!isNew && <button type="button" onClick={async () => { if (window.confirm("Delete this listing?")) { await adminApi(`/admin/listings/${id}`, { method: "DELETE" }); navigate("/admin/listings"); } }} className="text-white/40 hover:text-red-300 px-3" data-testid="listing-delete-button"><Trash2 size={15} /></button>}
          <button type="submit" disabled={busy} data-testid="listing-save-button" className="bg-gold text-black text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-sm hover:bg-gold-light disabled:opacity-50">{busy ? "Saving..." : "Save"}</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-surface border border-white/10 rounded-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelClass}>Category</label><select value={form.category} onChange={set("category")} className={`${inputClass} appearance-none`} data-testid="listing-category-select"><option value="aircraft" className="bg-surface">Aircraft</option><option value="yacht" className="bg-surface">Yacht</option></select></div>
          <div><label className={labelClass}>Status</label><select value={form.status} onChange={set("status")} className={`${inputClass} appearance-none`} data-testid="listing-status-select">{["draft", "live", "under_offer", "sold"].map((s) => <option key={s} value={s} className="bg-surface">{s.replace("_", " ")}</option>)}</select></div>
          <div className="sm:col-span-2"><label className={labelClass}>Title</label><input required value={form.title} onChange={set("title")} placeholder="e.g. Gulfstream G650" className={inputClass} data-testid="listing-title-input" /></div>
          <div><label className={labelClass}>Year</label><input type="number" value={form.year} onChange={set("year")} className={inputClass} data-testid="listing-year-input" /></div>
          <div><label className={labelClass}>Location</label><input value={form.location || ""} onChange={set("location")} className={inputClass} data-testid="listing-location-input" /></div>
          <div><label className={labelClass}>Price (private - never shown on site)</label><input type="number" value={form.price} onChange={set("price")} placeholder="Internal reference only" className={inputClass} data-testid="listing-price-input" /></div>
          <div><label className={labelClass}>Currency</label><select value={form.currency} onChange={set("currency")} className={`${inputClass} appearance-none`} data-testid="listing-currency-select">{["GBP", "USD", "EUR"].map((c) => <option key={c} className="bg-surface">{c}</option>)}</select></div>
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.discreet} onChange={set("discreet")} className="accent-[#C5A059]" data-testid="listing-discreet-checkbox" /> Discreet / off-market badge</label>
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.is_example} onChange={set("is_example")} className="accent-[#C5A059]" data-testid="listing-example-checkbox" /> Mark as example listing</label>
          <div className="sm:col-span-2"><label className={labelClass}>Short summary (card)</label><input value={form.summary} onChange={set("summary")} maxLength={400} className={inputClass} data-testid="listing-summary-input" /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Key specs (one per line, "Label: value")</label><textarea rows={5} value={specText} onChange={(e) => setSpecText(e.target.value)} placeholder={"Total time: 2,850 hrs\nCycles: 1,120\nPassengers: 16"} className={`${inputClass} resize-y font-mono text-xs`} data-testid="listing-specs-textarea" /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Description</label><textarea rows={8} value={form.description} onChange={set("description")} className={`${inputClass} resize-y`} data-testid="listing-description-textarea" /></div>
        </div>

        <div className="bg-surface border border-white/10 rounded-sm p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Photos · {photos.length}/12</p>
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} onClick={() => fileRef.current?.click()} data-testid="listing-photo-dropzone"
               className={`border border-dashed rounded-sm p-6 text-center text-xs cursor-pointer transition-colors ${isNew ? "border-white/10 text-white/30" : "border-white/20 text-white/50 hover:border-gold hover:text-gold"}`}>
            <Upload size={18} className="mx-auto mb-2" />
            {isNew ? "Save the listing first to add photos" : uploading ? `Uploading ${uploading}...` : "Drag & drop or click · JPG, PNG, WebP · 10 MB max"}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="listing-photo-input" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {photos.map((p, i) => (
              <div key={p.id} className="relative group aspect-[4/3] rounded-sm overflow-hidden border border-white/10" data-testid={`listing-photo-${i}`}>
                <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wider bg-gold text-black px-1.5 py-0.5">Cover</span>}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {i !== 0 && <button type="button" onClick={() => makeCover(p.id)} title="Make cover" className="w-8 h-8 rounded-full bg-white/10 hover:bg-gold hover:text-black flex items-center justify-center" data-testid={`listing-photo-cover-${i}`}><Star size={13} /></button>}
                  <button type="button" onClick={() => removePhoto(p.id)} title="Remove" className="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center" data-testid={`listing-photo-remove-${i}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </form>
  );
}
